/**
 * Typed API client for the incoming-email surface:
 *  - IMAP accounts (internal/messaging): list + connect.
 *  - Advanced automation (internal/imapadv): filter rules + auto-reply.
 *
 * Filter rules carry free-form `matchers`/`actions` JSON arrays; the UI builds
 * simple structured shapes but the backend stores them verbatim.
 */

import { apiClient } from '@/lib/apiClient';

export interface ImapAccount {
  id: string;
  userId: string;
  provider: string;
  email: string;
  lastSyncedAt?: string | null;
  enabled: boolean;
  createdAt: string;
}

export async function listImapAccounts(): Promise<ImapAccount[]> {
  const { data } = await apiClient.get<ImapAccount[]>('/imap/accounts');
  return data;
}

export interface ConnectAccountRequest {
  userId: string;
  provider: string;
  email: string;
  refreshToken?: string;
}

export async function connectImapAccount(req: ConnectAccountRequest): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>('/imap/accounts', req);
  return data;
}

export interface RuleMatcher {
  field: string;
  op: string;
  value: string;
}

export interface RuleAction {
  type: string;
  value: string;
}

export interface FilterRule {
  id: string;
  name: string;
  matchers: RuleMatcher[];
  actions: RuleAction[];
  enabled: boolean;
  sortOrder: number;
}

export async function listRules(accountId: string): Promise<FilterRule[]> {
  const { data } = await apiClient.get<FilterRule[]>(`/imap/accounts/${accountId}/rules`);
  return data;
}

export interface CreateRuleRequest {
  name: string;
  matchers: RuleMatcher[];
  actions: RuleAction[];
  sortOrder?: number;
}

export async function createRule(accountId: string, req: CreateRuleRequest): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>(`/imap/accounts/${accountId}/rules`, req);
  return data;
}

export interface AutoReplyRequest {
  subject: string;
  body: string;
  startsAt?: string;
  endsAt?: string;
}

export async function setAutoReply(accountId: string, req: AutoReplyRequest): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>(`/imap/accounts/${accountId}/auto-reply`, req);
  return data;
}
